import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/ui/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@repo/ui/components/ui/dialog";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Shared reject dialog for places and features.
 *
 * Backend already stores `reason` on `status_reason` and in the
 * `moderation_events.reason` column — this component is pure UI.
 *
 * Presets click-to-fill the textarea so the admin can still tweak the wording.
 */
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
const PLACE_PRESETS = ["Dålig bildkvalitet (suddig / mörk / oläsbar)", "Duplicerad plats", "Fel position", "Platsen finns inte", "Fel namn eller identitet", "Olämpligt innehåll", "Saknar relevant information"];
const FEATURE_PRESETS = ["Dålig bildkvalitet (suddig / mörk / oläsbar)", "Bilden matchar inte uppgifterna", "Duplicerad funktion", "Fel position", "Inte en tillgänglighetsfunktion", "Fel funktionstyp", "Olämpligt innehåll"];
export function RejectDialog(t0) {
  const $ = _c(59);
  const {
    open,
    entityType,
    targetName,
    cascadeFeatureCount: t1,
    isSubmitting,
    onClose,
    onSubmit
  } = t0;
  const cascadeFeatureCount = t1 === undefined ? 0 : t1;
  const [reason, setReason] = useState("");
  let t2;
  if ($[0] !== open) {
    t2 = () => {
      if (open) {
        setReason("");
      }
    };
    $[0] = open;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== open || $[3] !== targetName) {
    t3 = [open, targetName];
    $[2] = open;
    $[3] = targetName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  const presets = entityType === "place" ? PLACE_PRESETS : FEATURE_PRESETS;
  const label = entityType === "place" ? "plats" : "funktion";
  let t4;
  if ($[5] !== isSubmitting || $[6] !== onClose) {
    t4 = next => {
      if (!next && !isSubmitting) {
        onClose();
      }
    };
    $[5] = isSubmitting;
    $[6] = onClose;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== label) {
    t5 = /*#__PURE__*/_jsxs(DialogTitle, {
      children: ["Avvisa ", label]
    });
    $[8] = label;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== targetName) {
    t6 = targetName ? /*#__PURE__*/_jsx("strong", {
      children: targetName
    }) : null;
    $[10] = targetName;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = /*#__PURE__*/_jsxs(DialogDescription, {
      children: [t6, " kommer markeras som avvisad och d\xF6ljas fr\xE5n kartan. H\xE4ndelsen sparas i granskningshistoriken."]
    });
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t7) {
    t8 = /*#__PURE__*/_jsxs(DialogHeader, {
      children: [t5, t7]
    });
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== cascadeFeatureCount || $[18] !== entityType) {
    t9 = entityType === "place" && cascadeFeatureCount > 0 && /*#__PURE__*/_jsxs("div", {
      className: "flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900",
      children: [/*#__PURE__*/_jsx(AlertTriangle, {
        className: "mt-0.5 h-4 w-4 shrink-0"
      }), /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx("strong", {
          children: cascadeFeatureCount
        }), " bifogad", cascadeFeatureCount === 1 ? "" : "e", " funktion", cascadeFeatureCount === 1 ? "" : "er", " kommer ocks\xE5 avvisas automatiskt."]
      })]
    });
    $[17] = cascadeFeatureCount;
    $[18] = entityType;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = /*#__PURE__*/_jsx("p", {
      className: "text-xs font-medium uppercase tracking-wider text-gray-500",
      children: "Vanliga anledningar"
    });
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isSubmitting || $[22] !== presets) {
    let t12;
    if ($[24] !== isSubmitting) {
      t12 = preset => /*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: () => setReason(preset),
        disabled: isSubmitting,
        className: "rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 transition-colors hover:border-brand hover:bg-brand/5 disabled:opacity-50",
        children: preset
      }, preset);
      $[24] = isSubmitting;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    t11 = presets.map(t12);
    $[21] = isSubmitting;
    $[22] = presets;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t10, /*#__PURE__*/_jsx("div", {
        className: "flex flex-wrap gap-1.5",
        children: t11
      })]
    });
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = /*#__PURE__*/_jsx("label", {
      htmlFor: "reject-reason",
      className: "text-xs font-medium uppercase tracking-wider text-gray-500",
      children: "Anledning (valfritt)"
    });
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = e => setReason(e.target.value);
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== isSubmitting || $[31] !== reason) {
    t15 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-1",
      children: [t13, /*#__PURE__*/_jsx("textarea", {
        id: "reject-reason",
        value: reason,
        onChange: t14,
        disabled: isSubmitting,
        rows: 3,
        placeholder: "Valfri notering \u2014 syns i granskningshistoriken.",
        className: "w-full resize-none rounded border border-gray-200 p-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      })]
    });
    $[30] = isSubmitting;
    $[31] = reason;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== isSubmitting || $[34] !== onClose) {
    t16 = /*#__PURE__*/_jsx(Button, {
      variant: "outline",
      onClick: onClose,
      disabled: isSubmitting,
      children: "Avbryt"
    });
    $[33] = isSubmitting;
    $[34] = onClose;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== onSubmit || $[37] !== reason) {
    t17 = () => onSubmit(reason.trim() || undefined);
    $[36] = onSubmit;
    $[37] = reason;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== isSubmitting) {
    t18 = isSubmitting ? /*#__PURE__*/_jsx(Loader2, {
      className: "h-3.5 w-3.5 animate-spin"
    }) : /*#__PURE__*/_jsx(Trash2, {
      className: "h-3.5 w-3.5"
    });
    $[39] = isSubmitting;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== isSubmitting || $[42] !== label || $[43] !== t17 || $[44] !== t18) {
    t19 = /*#__PURE__*/_jsxs(Button, {
      variant: "destructive",
      onClick: t17,
      disabled: isSubmitting,
      className: "gap-1.5",
      children: [t18, "Avvisa ", label]
    });
    $[41] = isSubmitting;
    $[42] = label;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t16 || $[47] !== t19) {
    t20 = /*#__PURE__*/_jsxs(DialogFooter, {
      children: [t16, t19]
    });
    $[46] = t16;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t12 || $[50] !== t15 || $[51] !== t20 || $[52] !== t8 || $[53] !== t9) {
    t21 = /*#__PURE__*/_jsxs(DialogContent, {
      className: "max-w-lg",
      children: [t8, t9, t12, t15, t20]
    });
    $[49] = t12;
    $[50] = t15;
    $[51] = t20;
    $[52] = t8;
    $[53] = t9;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== open || $[56] !== t21 || $[57] !== t4) {
    t22 = /*#__PURE__*/_jsx(Dialog, {
      open: open,
      onOpenChange: t4,
      children: t21
    });
    $[55] = open;
    $[56] = t21;
    $[57] = t4;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  return t22;
}